import React from 'react';
import { Tab } from '@components/atoms/Tab';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { EmptyData } from '@components/molecules/EmptyData';
import { Pagination } from '@components/molecules/Pagination';
import { TaskDetailModal } from '@components/organisms/TaskDetailModal';
import Search from '@components/ui/Search';
import ReactSelect from '@components/atoms/ReactSelect';
import { Task } from '@models/task.model';
import { useTask } from '@hooks/api/task/useTask';
import { getTaskStatusDisplay, translateTaskDisplayValue } from '@utils/taskDisplay';
import { formatDateOnly } from '@utils/formatDate';
import { useTranslation } from '@hooks/useTranslation';

interface AssignedTasksProps {
  tasks: Task[];
  onManageTasks?: () => void;
  disabled?: boolean;
}

const ALL = '__all__';
const PAGE_SIZE = 10;

export const AssignedTasks: React.FC<AssignedTasksProps> = ({
  tasks,
  onManageTasks,
  disabled,
}) => {
  const [search, setSearch] = React.useState('');
  const [projectFilter, setProjectFilter] = React.useState<string>(ALL);
  const [statusFilter, setStatusFilter] = React.useState<string>(ALL);
  const [page, setPage] = React.useState(1);
  const [isDetailOpen, setIsDetailOpen] = React.useState(false);

  // The crew query carries only enough of each task to fill this table, so the
  // detail panel refetches the full record on click.
  const { task: detailTask, fetchTask, setTask } = useTask();

  // Sprint 28 change request (global sweep, defect fix). `display.label` used to be raw English
  // text — see `@utils/taskDisplay.ts`'s `TaskDisplayInfo.label` comment.
  const tTaskDetail = useTranslation('taskDetail');

  const allTasks = React.useMemo(() => tasks ?? [], [tasks]);

  /** Label each task once — the status label doubles as filter and column. */
  const rows = React.useMemo(
    () =>
      allTasks.map((task) => {
        const display = getTaskStatusDisplay(task);
        return {
          task,
          statusLabel: translateTaskDisplayValue(display.displayValue, tTaskDetail),
          statusColor: display.color,
        };
      }),
    [allTasks, tTaskDetail],
  );

  const projectOptions = React.useMemo(() => {
    const byId = new Map<string, string>();
    allTasks.forEach((task) => {
      if (task.projectId && !byId.has(task.projectId)) {
        byId.set(task.projectId, task.project?.name || 'Unknown project');
      }
    });
    return [
      { value: ALL, label: 'All projects' },
      ...Array.from(byId.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [allTasks]);

  // Built from what the crew actually has, so the dropdown never offers a
  // status that would return an empty table.
  const statusOptions = React.useMemo(
    () => [
      { value: ALL, label: 'All statuses' },
      ...Array.from(new Set(rows.map((row) => row.statusLabel)))
        .sort((a, b) => a.localeCompare(b))
        .map((label) => ({ value: label, label })),
    ],
    [rows],
  );

  const term = search.trim().toLowerCase();
  const filtered = rows.filter(({ task, statusLabel }) => {
    if (term && !(task.name || '').toLowerCase().includes(term)) return false;
    if (projectFilter !== ALL && task.projectId !== projectFilter) return false;
    if (statusFilter !== ALL && statusLabel !== statusFilter) return false;
    return true;
  });

  // Filtering can strip out the page the user was on; snap back rather than
  // leaving them staring at an empty table.
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  React.useEffect(() => setPage(1), [term, projectFilter, statusFilter]);

  const visible = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const hasFilters =
    Boolean(term) || projectFilter !== ALL || statusFilter !== ALL;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 mt-6 items-end justify-between">
        <Tab
          tabs={[
            {
              id: 'assignedTasks',
              name: 'Assigned Tasks',
              leftIcon: <ListAltIcon className="w-4 h-4" />,
              count: allTasks.length,
            },
          ]}
          activeTab=""
          onTabChange={() => {}}
        />

        {onManageTasks && (
          <button
            type="button"
            onClick={onManageTasks}
            disabled={disabled}
            className="mb-2 shrink-0 text-xs font-semibold text-indigo-600 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900/40 rounded-md px-3 py-1.5 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed">
            Manage Tasks
          </button>
        )}
      </div>

      {allTasks.length === 0 ? (
        <div className="min-h-60">
          <EmptyData description="No Assigned Tasks" />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <Search
              placeholder="Search tasks by name..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ReactSelect
                options={projectOptions}
                value={projectOptions.find(
                  (option) => option.value === projectFilter,
                )}
                isClearable={false}
                onChange={(option: any) => setProjectFilter(option?.value || ALL)}
              />
              <ReactSelect
                options={statusOptions}
                value={statusOptions.find(
                  (option) => option.value === statusFilter,
                )}
                isClearable={false}
                onChange={(option: any) => setStatusFilter(option?.value || ALL)}
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-center text-gray-500 dark:text-gray-400 py-8 m-0">
              No tasks match these filters.
            </p>
          ) : (
            <div className="relative overflow-x-auto shadow-md sm:rounded-lg ring-1 ring-gray-900/5 dark:ring-white/10">
              <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-4 py-3">
                      Task
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Project
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-3">
                      Start
                    </th>
                    <th scope="col" className="px-4 py-3">
                      End
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(({ task, statusLabel, statusColor }) => (
                    <tr
                      key={
                        task.taskId ??
                        `${task.name}-${String(task.startTime)}-${String(task.endTime)}`
                      }
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                      onClick={() => {
                        if (!task.taskId) return;
                        fetchTask(task.taskId);
                        setIsDetailOpen(true);
                      }}>
                      <th
                        scope="row"
                        className="px-4 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                        {task.parentTaskId ? '↳ ' : ''}
                        {task.name}
                      </th>
                      <td className="px-4 py-4 max-w-40 truncate">
                        <span title={task.project?.name}>
                          {task.project?.name || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className="inline-flex items-center gap-1.5 whitespace-nowrap"
                          title={statusLabel}>
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: statusColor }}
                          />
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {formatDateOnly(task.startTime)}
                      </td>
                      <td className="px-4 py-4">
                        {formatDateOnly(task.endTime)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* The shared Pagination always renders, so a single page would show
              "Page 1 of 1" with two dead arrows. */}
          {filtered.length > PAGE_SIZE && (
            <Pagination
              page={currentPage}
              take={PAGE_SIZE}
              total={filtered.length}
              setPage={setPage}
            />
          )}

          {hasFilters && filtered.length <= PAGE_SIZE && filtered.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 m-0">
              Showing {filtered.length} of {allTasks.length} tasks.
            </p>
          )}
        </>
      )}

      <TaskDetailModal
        task={detailTask}
        project={detailTask?.project}
        isTaskDetailModal={isDetailOpen}
        setIsTaskDetailModal={(isOpen) => {
          setIsDetailOpen(isOpen);
          if (!isOpen) setTask(null);
        }}
      />
    </div>
  );
};
